import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import type { CreativeParams } from '../../creatives/api';
import { getCreatives } from '../../creatives/api';
import queryKeys from './queryKeys';
import type {
  HypothesisCreativeEntry,
  HypothesisSection,
  UploadedFilter,
} from '../types/hypothesis';

export interface TestCreativesParams {
  app?: string;
  hypothesis?: string[];
  customer?: string;
  flow?: string[];
  technology?: string[];
  status?: string;
  search?: string;
  tpc_finish_date?: string;
  uploaded?: UploadedFilter;
}

const getTestCreatives = async (
  { params }: { params: TestCreativesParams },
  signal?: AbortSignal,
): Promise<HypothesisCreativeEntry[]> => {
  const { app, uploaded, ...filters } = params;
  const creativeParams: CreativeParams = {
    app,
    with_hypothesis: true,
    ...Object.fromEntries(
      Object.entries(filters).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : !!v,
      ),
    ),
    ...(uploaded && uploaded !== 'all' ? { upload_date: uploaded } : {}),
  };
  const data = await getCreatives({ params: creativeParams }, signal);
  // Backend returns the hypothesis bucket as `test_period`; the UI groups by `section`.
  return (
    data as unknown as (Omit<HypothesisCreativeEntry, 'section'> & {
      test_period?: HypothesisSection | null;
      section?: HypothesisSection | null;
    })[]
  ).map(({ test_period, section, ...rest }) => ({
    ...rest,
    section: section ?? test_period ?? null,
  }));
};

interface UseTestCreativesOptions {
  params: TestCreativesParams;
  config?: SynchronousQueryConfig<typeof getTestCreatives, HypothesisCreativeEntry[]>;
}

export const useTestCreatives = ({ params, config }: UseTestCreativesOptions) => {
  return useQuery({
    staleTime: 0,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getTestCreatives({ params }, signal),
    queryKey: queryKeys.testCreatives<TestCreativesParams>(params),
  });
};
